import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, GraduationCap, Users, Target, ExternalLink, ArrowLeft } from 'lucide-react';

export default function About() {
  const navigate = useNavigate();

  const sources = [
    {
      name: "İsmail Acarkan",
      role: "Enneagram Uzmanı ve Eğitmen",
      description: "Türkiye'de Enneagram'ın öncü isimlerinden. Kişilik tipleri ve mizaç analizi konusunda uzman. 'Enneagram: Eşini Keşfet' kitabının yazarı.",
      link: "https://www.amazon.com.tr/Enneagram-E%C5%9Fini-Ke%C5%9Ffet-%C4%B0smail-Acarkan/dp/6050845395"
    },
    {
      name: "Ziya Selçuk",
      role: "Eğitim Bilimci ve Yazar",
      description: "Eğitim psikolojisi ve öğrenci gelişimi alanında önemli çalışmalar yapmış akademisyen.",
      link: null
    },
    {
      name: "Eduen.com.tr",
      role: "Enneagram Eğitim Platformu",
      description: "Türkiye'nin önde gelen Enneagram eğitim ve danışmanlık platformu.",
      link: "https://www.eduen.com.tr"
    },
    {
      name: "The Enneagram Institute",
      role: "Uluslararası Enneagram Otoritesi",
      description: "Riso-Hudson Enneagram Tip Göstergesi (RHETI) geliştiricileri. Dünya çapında Enneagram araştırmaları yapan kurum.",
      link: "https://www.enneagraminstitute.com"
    },
    {
      name: "Integrative9",
      role: "Enneagram Araştırma ve Eğitim Merkezi",
      description: "Enneagram sisteminin bütünleştirici yaklaşımını benimseyen uluslararası eğitim ve araştırma platformu.",
      link: "https://www.integrative9.com/enneagram/"
    }
  ];

  const methodology = [
    {
      title: "Bilimsel Temel",
      icon: GraduationCap,
      description: "Enneagram sistemi, antik bilgelik gelenekleri ile modern psikoloji bilimini birleştiren, insanın dokuz temel kişilik tipini tanımlayan bir sistemdir. Mizaç360, bu sistemi Türk eğitim sistemi ve kültürüne uyarlamıştır."
    },
    {
      title: "Değerlendirme Süreci",
      icon: Target,
      description: "Kullanıcılarımız, dokuz mizaç tipinin özelliklerini ölçen güvenilir bir envanter doldurur. Her soru, titizlikle seçilmiş ve Türkçe'ye adapte edilmiştir. Puanlama sistemi, bireyin baskın mizaç tipini ve diğer tiplere olan eğilimlerini ortaya çıkarır."
    },
    {
      title: "AI Destekli Raporlama",
      icon: BookOpen,
      description: "Sonuçlar, yapay zeka destekli sistemimiz tarafından analiz edilir ve her kullanıcıya özel, detaylı bir rapor hazırlanır. Bu raporlar, güçlü yönler, gelişim alanları ve pratik öneriler içerir."
    },
    {
      title: "Eğitim Odaklı Yaklaşım",
      icon: Users,
      description: "Mizaç360, özellikle eğitim kurumları için tasarlanmıştır. Öğretmenler, sınıf dinamiklerini daha iyi anlayabilir; veliler, çocuklarına daha uygun rehberlik edebilir; öğrenciler ise kendilerini keşfedebilir."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-800">Mizaç360</h1>
          </div>
          <nav className="flex items-center gap-6">
            <a href="/resources" className="text-slate-600 hover:text-blue-600 transition font-medium">Kaynaklar</a>
            <a href="/blog" className="text-slate-600 hover:text-blue-600 transition font-medium">Blog</a>
            <a href="/news" className="text-slate-600 hover:text-blue-600 transition font-medium">Haberler</a>
            <a href="/about" className="text-slate-700 hover:text-blue-600 transition font-medium">Hakkımızda</a>
            <Button variant="outline" onClick={() => navigate('/')}>Ana Sayfa</Button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
            Hakkımızda & Metodoloji
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Mizaç360, bilimsel temellere dayanan Enneagram sistemini eğitim dünyasına taşıyan,
            öğrenci, veli ve öğretmenlere kendilerini keşfetme ve birbirlerini daha iyi anlama
            fırsatı sunan bir platformdur.
          </p>
        </div>

        {/* Methodology Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-slate-800 mb-3">Metodolojimiz</h3>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Mizaç360'ın bilimsel temelleri ve çalışma prensiplerimiz
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {methodology.map((item, index) => (
              <Card key={index} className="shadow-lg border-none hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-emerald-100 rounded-lg flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl">{item.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Enneagram Info Section */}
        <section className="mb-16">
          <Card className="shadow-xl border-none bg-gradient-to-r from-blue-50 to-emerald-50">
            <CardHeader>
              <CardTitle className="text-2xl">Enneagram Nedir?</CardTitle>
              <CardDescription>Dokuz mizaç tipinin bilimsel temelleri</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700 leading-relaxed">
                <strong>Enneagram</strong>, Yunanca'da "dokuz" anlamına gelen "ennea" ve "çizim" anlamına gelen "gram" 
                kelimelerinden oluşur. Dokuz temel kişilik tipini tanımlayan bu sistem, antik bilgelik geleneklerinden 
                modern psikolojiye uzanan köklere sahiptir.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Her insan, dokuz tipten birine baskın olarak sahiptir, ancak diğer tiplerin özelliklerini de farklı 
                derecelerde taşır. Bu tipler, kişinin dünyayı nasıl algıladığını, karar verme süreçlerini, ilişkilerini 
                ve stresle başa çıkma yöntemlerini şekillendirir.
              </p>
              <p className="text-slate-700 leading-relaxed">
                <strong>Eğitimde Enneagram:</strong> Öğretmenler, öğrencilerin mizaç tiplerini anlayarak daha etkili 
                öğretim stratejileri geliştirebilir. Veliler, çocuklarının doğal eğilimlerini keşfederek onlara daha 
                uygun destek sağlayabilir. Öğrenciler ise kendilerini tanıyarak güçlü yönlerini geliştirir ve zayıf 
                yönleriyle başa çıkmayı öğrenirler.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Academic Sources Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-slate-800 mb-3">Kaynak ve Referanslarımız</h3>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Mizaç360, alanında uzman isimlerin ve kurumların çalışmalarından yararlanmaktadır
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sources.map((source, index) => (
              <Card key={index} className="shadow-lg border-none hover:shadow-xl transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span>{source.name}</span>
                    {source.link && (
                      <a 
                        href={source.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </a>
                    )}
                  </CardTitle>
                  <CardDescription className="font-semibold">{source.role}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm leading-relaxed">{source.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Additional Academic Info */}
        <section className="mb-16">
          <Card className="shadow-xl border-none">
            <CardHeader>
              <CardTitle className="text-2xl">Akademik ve Bilimsel Dayanaklar</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Psikolojik Temeller</h4>
                <p className="text-slate-600 leading-relaxed">
                  Enneagram sistemi, modern psikolojinin kişilik teorileri ile uyumludur. Özellikle Carl Jung'ın 
                  arketip teorisi, Karen Horney'in nevrotik eğilimler çalışması ve Oscar Ichazo ile Claudio Naranjo'nun 
                  katkılarıyla bilimsel bir çerçeveye oturtulmuştur.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Eğitim Bilimleri Perspektifi</h4>
                <p className="text-slate-600 leading-relaxed">
                  Ziya Selçuk'un eğitim psikolojisi alanındaki çalışmaları, Enneagram'ın Türk eğitim sistemine 
                  uyarlanmasında önemli bir referans noktasıdır. Öğrenci gelişimi, öğretmen-öğrenci etkileşimi ve 
                  farklılaştırılmış öğretim stratejileri konularında Enneagram'ın pratik değeri vurgulanmıştır.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Türkiye'de Enneagram</h4>
                <p className="text-slate-600 leading-relaxed">
                  İsmail Acarkan, Türkiye'de Enneagram'ın tanınması ve yaygınlaşmasında öncü rol oynamıştır. 
                  Enedu.com platformu aracılığıyla binlerce kişiye ulaşan eğitimler, danışmanlıklar ve sertifika 
                  programları, Mizaç360'ın Türkçe içerik ve metodolojisinin oluşturulmasında temel kaynak olmuştur.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Uluslararası Standartlar</h4>
                <p className="text-slate-600 leading-relaxed">
                  The Enneagram Institute (Riso-Hudson), dünya çapında kabul görmüş RHETI (Riso-Hudson Enneagram Type 
                  Indicator) değerlendirme aracını geliştirmiştir. Mizaç360'ın envanter yapısı, bu uluslararası 
                  standartlar göz önünde bulundurularak Türk kültürüne adapte edilmiştir.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <Card className="shadow-xl border-none bg-gradient-to-r from-blue-600 to-emerald-600 text-white">
            <CardContent className="py-12">
              <h3 className="text-3xl font-bold mb-4">Kendinizi Keşfetmeye Hazır mısınız?</h3>
              <p className="text-lg mb-8 text-blue-50">
                Mizaç360 ile kişisel gelişim yolculuğunuza bugün başlayın
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button 
                  size="lg" 
                  variant="secondary"
                  className="bg-white text-blue-600 hover:bg-blue-50"
                  onClick={() => navigate('/register')}
                >
                  Ücretsiz Başla
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => navigate('/')}
                >
                  Ana Sayfaya Dön
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-300">
            © 2025 Mizaç360. Tüm hakları saklıdır.
          </p>
          <p className="text-sm text-slate-400 mt-2">
            Bilimsel kaynaklar: İsmail Acarkan, Ziya Selçuk, Enedu.com, The Enneagram Institute
          </p>
        </div>
      </footer>
    </div>
  );
}
